import { Injectable } from '@angular/core';
import { Http, QueryBus, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { deserializeInventoryDocumentBuyerModel, deserializeInventoryDocumentClientModel, serializeDocumentExchangeRateModel, serializeInventoryDocumentBuyerModel, serializeInventoryDocumentClientModel } from '@skylab-shared/business';
import { HttpService, ObservableCollection, Repository, RepositoryBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { CalculationMethod, ExchangeRateModel, InventoryDocumentItemModel, InventoryGetGoodsReceiptDocumentDTO, InventoryGoodsReceiptDocumentModel, InventoryUpdateGoodsReceiptDocumentDTO, RelatedEntity, VatTableItemModel } from '@skylab-shared/dataModel';
import { getStartOfDate, pickBy } from '@skylab-shared/utils';
import { ProductRepository } from '../product/product.repo';
import { PurchaseInvoiceDocumentRepository } from '../purchase/purchase-invoice-document.repo';
import { InventoryGoodsReceiptDocumentBO } from './inventory-goods-receipt-document.bo';

@Repository({
    boType: InventoryGoodsReceiptDocumentBO,
    apiController: TaggedUrl.join('inventory/goodsReceipt', [Tags.Api]),
    dto: {
        get: InventoryGetGoodsReceiptDocumentDTO,
        update: InventoryUpdateGoodsReceiptDocumentDTO
    }
})
@Injectable({ providedIn: 'root' })
export class InventoryGoodsReceiptDocumentRepository extends RepositoryBase<InventoryGoodsReceiptDocumentBO, InventoryGoodsReceiptDocumentModel, InventoryGetGoodsReceiptDocumentDTO, InventoryUpdateGoodsReceiptDocumentDTO> {

    public constructor(
        private readonly weaver: Weaver,
        private readonly queryBus: QueryBus,
        private readonly productRepository: ProductRepository,
        private readonly purchaseInvoiceRepository: PurchaseInvoiceDocumentRepository,
        http: Http
    ) {
        super(http as HttpService);
    }

    protected createBO(): InventoryGoodsReceiptDocumentBO {
        return new InventoryGoodsReceiptDocumentBO(this.weaver as WeaverBase, this.productRepository, this.queryBus as any, this.purchaseInvoiceRepository);
    }

    //#region Mapping
    protected async preparePlainObjectAsync(model: InventoryGoodsReceiptDocumentModel): Promise<InventoryUpdateGoodsReceiptDocumentDTO> {
        return {
            duplicatePreventionId: model.duplicatePreventionId,
            id: model.id,
            version: model.version,
            relatedCommercialDocuments: model.relatedCommercialDocuments.map(doc => ({
                id: doc.id,
                version: doc.version
            })) || [],
            creationMethod: model.creationMethod,
            currencyId: model.currencyId,
            calculationMethod: model.calculationMethod,
            costCalculationMethod: model.costCalculationMethod,
            issuer: model.issuer,
            recipient: model.recipient,
            note: model.note,
            issueDate: model.issueDate ? getStartOfDate(model.issueDate) : null,
            numberingId: model.numberingId,
            receiptDate: getStartOfDate(model.receiptDate),
            grossValue: model.grossValue,
            netValue: model.netValue,
            transactionLocation: model.transactionLocation,
            documentNumber: model.documentNumber,
            exchangeRate: model.exchangeRate ? serializeDocumentExchangeRateModel(model.exchangeRate) : null,
            items: model.items.map(elm => ({
                discountValue: elm.discountValue,
                discountKind: elm.discountKind,
                no: elm.ordinalNumber,
                note: elm.note,
                productId: elm.productId,
                productName: elm.productName,
                relatedCommercialItemsIds: elm.relatedCommercialItemsIds || [],
                pkwiu: elm.pkwiu,
                cnCode: elm.cnCode,
                symbol: elm.symbol,
                barCode: elm.barCode,
                jpkVatProductGroup: elm.jpkVatGroup,
                quantity: elm.quantity,
                uniqueKey: elm.uniqueKey,
                unitId: elm.unitId,
                price: model.calculationMethod === CalculationMethod.Gross ? elm.price_grossBeforeDiscount : elm.price_netBeforeDiscount,
                vatRateId: elm.vatRateId
            })),
            inventoryId: model.inventoryId,
            kind: model.kind,
            seller: serializeInventoryDocumentClientModel(model.seller),
            supplier: serializeInventoryDocumentBuyerModel(model.supplier)
        };
    }

    protected async fromPlainObjectAsync(item: InventoryGetGoodsReceiptDocumentDTO): Promise<InventoryGoodsReceiptDocumentModel> {
        return new InventoryGoodsReceiptDocumentModel(pickBy<InventoryGoodsReceiptDocumentModel>({
            duplicatePreventionId: item.duplicatePreventionId,
            id: item.id,
            version: item.version,
            note: item.note,
            relatedCommercialDocuments: item.relatedCommercialDocuments.map(doc => new RelatedEntity(doc.id, doc.version)) || [],
            currencyId: item.currencyId,
            calculationMethod: item.calculationMethod,
            costCalculationMethod: item.costCalculationMethod,
            grossValue: item.grossValue,
            netValue: item.netValue,
            issuer: item.issuer,
            recipient: item.recipient,
            issueDate: item.issueDate,
            numberingId: item.numberingId,
            kind: item.kind,
            receiptDate: item.receiptDate,
            transactionLocation: item.transactionLocation,
            issuedByVatPayer: item.issuedByVatPayer,
            documentNumber: item.documentNumber,
            inventoryId: item.inventoryId,
            items: new ObservableCollection(item.items.map(documentItem => {
                return new InventoryDocumentItemModel(pickBy<Partial<InventoryDocumentItemModel>>({
                    ordinalNumber: documentItem.no,
                    discountKind: documentItem.discountKind,
                    discountValue: documentItem.discountValue,
                    note: documentItem.note,
                    price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                    price_grossBeforeDiscount: documentItem.unitGrossPrice,
                    price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                    price_netBeforeDiscount: documentItem.unitNetPrice,
                    id: documentItem.id,
                    productId: documentItem.productId,
                    productName: documentItem.productName,
                    relatedCommercialItemsIds: documentItem.relatedCommercialItemsIds || [],
                    pkwiu: documentItem.pkwiu,
                    cnCode: documentItem.cnCode,
                    jpkVatGroups: documentItem.jpkVatProductGroup ? [documentItem.jpkVatProductGroup] : [],
                    quantity: documentItem.quantity,
                    uniqueKey: documentItem.uniqueKey,
                    unitId: documentItem.unitId,
                    symbol: documentItem.symbol,
                    barCode: documentItem.barCode,
                    value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                    value_grossBeforeDiscount: documentItem.grossValue,
                    value_netAfterDiscount: documentItem.netValueAfterDiscount,
                    value_netBeforeDiscount: documentItem.netValue,
                    value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                    value_vatBeforeDiscount: documentItem.vatValue,
                    vatRateId: documentItem.vatRateId,
                    inventoryTotalCost: documentItem.inventoryTotalCost,
                    inventoryUnitCost: documentItem.inventoryUnitCost,
                }, val => val !== void 0));
            })),
            seller: deserializeInventoryDocumentClientModel(item.seller),
            supplier: deserializeInventoryDocumentBuyerModel(item.supplier),
            exchangeRate: item.exchangeRate ? new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                manual: item.exchangeRate.manual,
                rate: item.exchangeRate.rate,
                tableNo: item.exchangeRate.tableNo,
                units: item.exchangeRate.units
            }, val => val !== void 0)) : null,
            vatTableItems: new ObservableCollection(item.vatTable.items.map(item => {
                return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                    grossValue: item.currencyGross ?? item.gross,
                    netValue: item.currencyNet ?? item.net,
                    vatRateId: item.vatRateId,
                    vatValue: item.currencyVat ?? item.vat,
                    cost: item.cost
                }, val => val !== void 0));
            })),
            vatTableItemsInBaseCurrency: item.exchangeRate ? new ObservableCollection(item.vatTable.items.map(item => {
                return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                    grossValue: item.gross,
                    netValue: item.net,
                    vatRateId: item.vatRateId,
                    vatValue: item.vat,
                    cost: item.cost
                }, val => val !== void 0));
            })) : new ObservableCollection()
        }, val => val !== void 0));
    }
    //#endregion
}
